import { defineStore } from 'pinia'
import axios from 'axios'

interface ServiceItem {
  id: number
  name: string
  description: string
  price: string
}

interface ServicePlan {
  id: number
  name: string
  description: string
  price: string
  service: number
  duration: number
  duration_type: string
  duration_type_display: string
  is_active: boolean
  is_free_trial: boolean
}

export interface Service {
  id: number
  name: string
  description: string
  type: string
  status: string
  stability: string
  price: string
  is_a_paid_service: boolean
  currency_symbol: string
  items: ServiceItem[]
  plans: ServicePlan[]
}

interface Subscription {
  id: number
  service: number
  service_name: string
  service_type: string
  plan: number
  plan_name: string
  plan_duration: number
  plan_duration_type: string
  start_date: string
  end_date: string
  is_active: boolean
  payment_status: string
  payment_method: string
  amount: string
  transaction_id: string
}

interface SubscriptionPayload {
  service: number
  plan: number
  payment_method: string
  phone_number?: string
  customer?: Record<string, string>
}

export const useServicesStore = defineStore('services', {
  state: () => ({
    services: [] as Service[],
    subscriptions: [] as Subscription[],
    servicesPagination: {
      count: 0,
      next: null,
      previous: null,
      currentPage: 1,
      totalPages: 1,
      pageSize: 10,
    },
    subscriptionsPagination: {
      count: 0,
      next: null,
      previous: null,
      currentPage: 1,
      totalPages: 1,
      pageSize: 10,
    },
    loading: false,
    error: null as string | null,
    fxRate: null as number | null,
    fxFetchedAt: null as number | null,
    checkoutReference: null as string | null,
  }),

  getters: {
    getServiceById: (state) => (id: number) => {
      return state.services.find((service) => service.id === id)
    },

    activeSubscriptions: (state) => {
      return state.subscriptions.filter((subscription) => subscription.is_active)
    },

    isSubscribedToService: (state) => (serviceId: number) => {
      return (
        Array.isArray(state.subscriptions) &&
        state.subscriptions.some((subscription) => subscription.service === serviceId && subscription.is_active)
      )
    },
  },

  actions: {
    async fetchServices(page: number = 1, pageSize: number = 10, filters: any = {}) {
      this.loading = true
      this.error = null

      try {
        const url = import.meta.env.VITE_APP_API_BASE_URL + import.meta.env.VITE_APP_SERVICE_API_URL

        // Build query parameters
        const params = new URLSearchParams({
          page: page.toString(),
          page_size: pageSize.toString(),
          ...filters,
        })

        const response = await axios.get(`${url}?${params}`)

        if (response.data && response.data.results) {
          this.services = response.data.results
          this.servicesPagination = {
            count: response.data.count || 0,
            next: response.data.next,
            previous: response.data.previous,
            currentPage: page,
            totalPages: Math.ceil((response.data.count || 0) / pageSize),
            pageSize: pageSize,
          }
        } else {
          // Fallback for non-paginated response
          this.services = response.data
        }
      } catch (error: any) {
        this.error = error.message || 'Failed to fetch services'
        console.error('Error fetching services:', error)
      } finally {
        this.loading = false
      }
    },

    async fetchSubscriptions(page: number = 1, pageSize: number = 10, filters: any = {}) {
      this.loading = true
      this.error = null

      try {
        const url = import.meta.env.VITE_APP_API_BASE_URL + import.meta.env.VITE_APP_SERVICE_SUBSCRIPTIONS_API_URL

        // Build query parameters
        const params = new URLSearchParams({
          page: page.toString(),
          page_size: pageSize.toString(),
          ...filters,
        })

        const response = await axios.get(`${url}?${params}`)

        if (response.data && response.data.results) {
          this.subscriptions = response.data.results
          this.subscriptionsPagination = {
            count: response.data.count || 0,
            next: response.data.next,
            previous: response.data.previous,
            currentPage: page,
            totalPages: Math.ceil((response.data.count || 0) / pageSize),
            pageSize: pageSize,
          }
        } else {
          // Fallback for non-paginated response
          this.subscriptions = response.data
        }
      } catch (error: any) {
        this.error = error.message || 'Failed to fetch subscriptions'
        console.error('Error fetching subscriptions:', error)
      } finally {
        this.loading = false
      }
    },

    async subscribeToService(payload: SubscriptionPayload) {
      this.loading = true
      this.error = null

      try {
        const url = import.meta.env.VITE_APP_API_BASE_URL + import.meta.env.VITE_APP_SERVICE_SUBSCRIPTIONS_API_URL
        const response = await axios.post(url, payload, { validateStatus: (s) => s < 500 })
        await this.fetchSubscriptions()

        if (response.status === 409) {
          // Existing pending payment — return its data so caller can resume the flow
          return { ...response.data, _existingPending: true }
        }
        if (response.status >= 400) {
          throw { response }
        }
        return response.data
      } catch (error: any) {
        this.error = error.message || 'Failed to subscribe to service'
        console.error('Error subscribing to service:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    async getServiceDetails(serviceId: number) {
      this.loading = true
      this.error = null

      try {
        const url = import.meta.env.VITE_APP_API_BASE_URL + import.meta.env.VITE_APP_SERVICE_API_URL + `${serviceId}/`
        const response = await axios.get(url)
        return response.data
      } catch (error: any) {
        this.error = error.message || 'Failed to fetch service details'
        console.error('Error fetching service details:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    async getPaymentStatus(reference: string) {
      const base =
        import.meta.env.VITE_APP_API_BASE_URL +
        (import.meta.env.VITE_APP_SERVICE_PAYMENT_STATUS_URL || 'services/payments/')
      const response = await axios.get(`${base}${reference}/status/`)
      return response.data
    },

    async fetchFxRate(force = false) {
      const FRESH_MS = 30 * 60 * 1000
      if (!force && this.fxRate && this.fxFetchedAt && Date.now() - this.fxFetchedAt < FRESH_MS) {
        return this.fxRate
      }
      try {
        const url =
          import.meta.env.VITE_APP_API_BASE_URL +
          (import.meta.env.VITE_APP_SERVICE_FX_RATE_URL || 'services/fx/usd-tzs/')
        const response = await axios.get(url)
        const rate = parseFloat(response.data.rate)
        if (!Number.isNaN(rate)) {
          this.fxRate = rate
          this.fxFetchedAt = Date.now()
        }
        return this.fxRate
      } catch (err) {
        console.warn('FX rate fetch failed:', err)
        return this.fxRate
      }
    },

    openCheckout(reference: string) {
      this.checkoutReference = reference
    },

    closeCheckout() {
      this.checkoutReference = null
    },

    convertUsdToTzs(usd: number | string): number | null {
      const n = typeof usd === 'string' ? parseFloat(usd) : usd
      if (!this.fxRate || Number.isNaN(n)) return null
      return Math.round(n * this.fxRate)
    },
  },
})
